import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { INR, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, FileText, ArrowLeft, Upload, X } from "lucide-react";

const MODES = ["Cash", "UPI", "NEFT", "RTGS", "Bank Transfer", "Cheque", "Card"];
const FALLBACK_FINANCERS = ["KAFL", "CHOLA", "IDFC", "SHRIRAM"];
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function FinanceDetail() {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [financers, setFinancers] = useState(FALLBACK_FINANCERS);
  const [addingFinancer, setAddingFinancer] = useState(false);
  const [newFinancer, setNewFinancer] = useState("");

  useEffect(() => {
    api.get(`/finance/${vehicleId}`).then(({ data }) => {
      setVehicle(data.vehicle);
      setF({ ...data.finance, payments: data.finance.payments || [] });
    });
    api.get("/financers").then(({ data }) => setFinancers(data.financers || FALLBACK_FINANCERS)).catch(() => {});
  }, [vehicleId]);

  const totals = useMemo(() => {
    if (!f) return { subtotal: 0, total: 0, down: 0, paid: 0, remain: 0 };
    const isCash = (f.payment_type || "Loan") === "Cash";
    const subtotal = Number(f.ex_showroom_price || 0) + Number(f.insurance_price || 0) +
                     Number(f.rto_charges || 0) + Number(f.permit_charges || 0) +
                     Number(f.earthing_charges || 0) + Number(f.file_charges || 0);
    const total = Math.max(subtotal - Number(f.discount || 0), 0);
    const paid = (f.payments || []).reduce((s, p) => s + Number(p.amount || 0), 0);
    // Loan mode: down = total - loan; remaining = down - paid (existing behavior, untouched)
    // Cash mode: no loan involved → remaining = total - paid
    const down = isCash ? 0 : Math.max(total - Number(f.loan_amount || 0), 0);
    const remain = isCash
      ? Math.max(total - paid, 0)
      : Math.max(down - paid, 0);
    return { subtotal, total, down, paid, remain };
  }, [f]);

  const setNum = (k) => (e) => setF({ ...f, [k]: e.target.value === "" ? "" : Number(e.target.value) });

  const addPayment = () => setF({ ...f, payments: [...(f.payments || []), { mode: "Cash", amount: 0, date: todayISO(), transaction_id: "", remarks: "" }] });
  const updatePayment = (i, k, v) => {
    const next = [...f.payments]; next[i] = { ...next[i], [k]: k === "amount" ? Number(v || 0) : v };
    setF({ ...f, payments: next });
  };
  const removePayment = (i) => setF({ ...f, payments: f.payments.filter((_, idx) => idx !== i) });

  const save = async () => {
    // Client-side validation (mirrors backend)
    if (!f.date_of_delivery) {
      toast.error("Please enter Date of Delivery before saving.");
      return;
    }
    const paymentType = f.payment_type || "Loan";
    if (paymentType === "Loan" && !(f.financer_name || "").trim()) {
      toast.error("Please select Financer Name before saving Finance Details.");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/finance/${vehicleId}`, {
        ex_showroom_price: Number(f.ex_showroom_price || 0),
        insurance_price: Number(f.insurance_price || 0),
        rto_charges: Number(f.rto_charges || 0),
        permit_charges: Number(f.permit_charges || 0),
        earthing_charges: Number(f.earthing_charges || 0),
        file_charges: Number(f.file_charges || 0),
        discount: Number(f.discount || 0),
        loan_amount: paymentType === "Loan" ? Number(f.loan_amount || 0) : 0,
        payment_type: paymentType,
        payments: (f.payments || []).map(p => ({ ...p, amount: Number(p.amount || 0) })),
        customer_name: f.customer_name || "",
        customer_phone: f.customer_phone || "",
        customer_address: f.customer_address || "",
        date_of_delivery: f.date_of_delivery || null,
        financer_name: paymentType === "Loan" ? (f.financer_name || null) : null,
        aadhar_doc: f.aadhar_doc || null,
        aadhar_back_doc: f.aadhar_back_doc || null,
        pan_doc: f.pan_doc || null,
        photo_doc: f.photo_doc || null,
        battery_doc: f.battery_doc || null,
        insurance_doc: f.insurance_doc || null,
        rto_doc: f.rto_doc || null,
      });
      toast.success("Finance saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const goToInvoice = () => {
    if (!f.date_of_delivery) {
      toast.error("Please enter Date of Delivery before generating invoice.");
      return;
    }
    const paymentType = f.payment_type || "Loan";
    if (paymentType === "Loan" && !(f.financer_name || "").trim()) {
      toast.error("Please select Financer Name before generating invoice.");
      return;
    }
    navigate(`/invoice/${vehicleId}`);
  };

  if (!vehicle || !f) return <div className="text-slate-500 text-sm">Loading finance…</div>;

  return (
    <div className="max-w-6xl mx-auto">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4" data-testid="back-button">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900" data-testid="finance-detail-heading">Finance Details</h1>
          <p className="text-sm text-slate-500 mt-1">{vehicle.model} · <span className="font-mono">{vehicle.chassis_number}</span> · {vehicle.branch}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={save} disabled={saving} data-testid="finance-save-button"
            className="px-4 py-2 rounded-md bg-brand-blue bg-brand-blue-hover text-white text-sm disabled:opacity-60">
            {saving ? "Saving…" : "Save"}
          </button>
          <button onClick={goToInvoice} data-testid="finance-view-invoice"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand-orange bg-brand-orange-hover text-white text-sm">
            <FileText className="w-4 h-4" /> View Invoice
          </button>
        </div>
      </div>

      {/* Vehicle Details */}
      <Section title="Vehicle Details (Auto)">
        <ReadRow k="Model" v={vehicle.model} />
        <ReadRow k="Chassis" v={vehicle.chassis_number} mono />
        <ReadRow k="Engine" v={vehicle.engine_number} mono />
        <ReadRow k="Branch" v={vehicle.branch} />
      </Section>

      {/* Customer */}
      <Section title="Customer Details">
        <FormRow label="Customer Name">
          <input className="input" value={f.customer_name || ""} onChange={(e) => setF({ ...f, customer_name: e.target.value })} data-testid="customer-name" />
        </FormRow>
        <FormRow label="Phone">
          <input className="input" value={f.customer_phone || ""} onChange={(e) => setF({ ...f, customer_phone: e.target.value })} data-testid="customer-phone" />
        </FormRow>
        <FormRow label="Date of Delivery *">
          <input type="date" className="input" value={f.date_of_delivery || ""}
            onChange={(e) => setF({ ...f, date_of_delivery: e.target.value || null })}
            required data-testid="date-of-delivery" />
          <div className="text-[10px] text-slate-400 mt-1">
            {f.date_of_delivery
              ? `Displayed as ${new Date(f.date_of_delivery).toLocaleDateString("en-GB")}`
              : "Mandatory. Displayed as DD/MM/YYYY."}
          </div>
        </FormRow>
        <FormRow label="Address" full>
          <input className="input" value={f.customer_address || ""} onChange={(e) => setF({ ...f, customer_address: e.target.value })} data-testid="customer-address" />
        </FormRow>
      </Section>

      {/* Documents */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 mb-6">
        <h3 className="font-heading text-lg font-semibold mb-1">Customer Documents</h3>
        <p className="text-xs text-slate-500 mb-4">Upload documents (max 2 MB each — JPG, PNG, PDF)</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <DocUpload label="Aadhaar Card — Front" field="aadhar_doc" f={f} setF={setF} testid="doc-aadhar-front" vehicleId={vehicleId} />
          <DocUpload label="Aadhaar Card — Back" field="aadhar_back_doc" f={f} setF={setF} testid="doc-aadhar-back" vehicleId={vehicleId} />
          <DocUpload label="PAN Card" field="pan_doc" f={f} setF={setF} testid="doc-pan" vehicleId={vehicleId} />
          <DocUpload label="Passport Photo" field="photo_doc" f={f} setF={setF} testid="doc-photo" vehicleId={vehicleId} />
          <DocUpload label="Battery Warranty Card" field="battery_doc" f={f} setF={setF} testid="doc-battery" vehicleId={vehicleId} />
          <DocUpload label="Insurance" field="insurance_doc" f={f} setF={setF} testid="doc-insurance" vehicleId={vehicleId} />
          <DocUpload label="RTO" field="rto_doc" f={f} setF={setF} testid="doc-rto" vehicleId={vehicleId} />
        </div>
      </div>

      {/* Price */}
      <Section title="Price Details">
        <PriceInput label="Ex-Showroom Price" val={f.ex_showroom_price} on={setNum("ex_showroom_price")} testid="price-exshowroom" />
        <PriceInput label="Insurance Price" val={f.insurance_price} on={setNum("insurance_price")} testid="price-insurance" />
        <PriceInput label="RTO Charges" val={f.rto_charges} on={setNum("rto_charges")} testid="price-rto" />
        <PriceInput label="Permit Charges" val={f.permit_charges} on={setNum("permit_charges")} testid="price-permit" />
        <PriceInput label="Earthing Charges" val={f.earthing_charges} on={setNum("earthing_charges")} testid="price-earthing" />
        <PriceInput label="File Charges" val={f.file_charges} on={setNum("file_charges")} testid="price-file" />
        <PriceInput label="Discount" val={f.discount} on={setNum("discount")} testid="price-discount" />
        <div className="col-span-2 md:col-span-3 bg-slate-100 rounded-md p-3 flex items-center justify-between text-sm">
          <span className="text-slate-600">Subtotal</span>
          <span className="font-mono text-slate-900" data-testid="finance-subtotal">{INR(totals.subtotal)}</span>
        </div>
        <div className="col-span-2 md:col-span-3 bg-blue-900 text-white rounded-md p-4 flex items-center justify-between">
          <span className="text-xs uppercase tracking-wider text-blue-200">Total Vehicle Price (after discount)</span>
          <span className="font-heading text-2xl font-bold" data-testid="total-vehicle-price">{INR(totals.total)}</span>
        </div>
      </Section>

      {/* Loan */}
      <Section title="Payment & Loan Details">
        <FormRow label="Payment Type *">
          <select className="input" value={f.payment_type || "Loan"}
            onChange={(e) => setF({ ...f, payment_type: e.target.value })}
            data-testid="payment-type">
            <option value="Loan">Loan</option>
            <option value="Cash">Cash</option>
          </select>
        </FormRow>

        {(f.payment_type || "Loan") === "Loan" && (
          <>
            <PriceInput label="Loan Amount *" val={f.loan_amount} on={setNum("loan_amount")} testid="loan-amount" />
            <FormRow label="Financer Name *">
              <div className="flex gap-1">
                <select className="input flex-1" value={f.financer_name || ""}
                  onChange={(e) => setF({ ...f, financer_name: e.target.value || null })}
                  data-testid="financer-name" required>
                  <option value="">— Select —</option>
                  {financers.map(fn => <option key={fn} value={fn}>{fn}</option>)}
                </select>
                <button type="button" onClick={() => setAddingFinancer(true)}
                  className="px-2 rounded-md border border-slate-300 hover:bg-slate-50 text-xs"
                  data-testid="add-financer-button" title="Add new financer">+</button>
              </div>
            </FormRow>
          </>
        )}

        {(f.payment_type || "Loan") === "Loan" && (
          <div className="col-span-2 md:col-span-1 bg-slate-100 rounded-md p-4 flex items-center justify-between" data-testid="down-payment-card">
            <span className="text-xs uppercase tracking-wider text-slate-500">Down Payment (auto)</span>
            <span className="font-heading text-2xl font-bold text-slate-900" data-testid="down-payment">{INR(totals.down)}</span>
          </div>
        )}
      </Section>

      {addingFinancer && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" data-testid="add-financer-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-6">
            <div className="font-heading text-lg font-semibold mb-3">Add Financer</div>
            <input className="input w-full" placeholder="Financer name"
              value={newFinancer} onChange={(e) => setNewFinancer(e.target.value)}
              data-testid="new-financer-input" />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => { setAddingFinancer(false); setNewFinancer(""); }}
                className="px-3 py-1.5 rounded-md text-sm border border-slate-300" data-testid="new-financer-cancel">Cancel</button>
              <button onClick={async () => {
                const name = (newFinancer || "").trim();
                if (!name) return;
                try {
                  await api.post("/financers", { name });
                  const { data } = await api.get("/financers");
                  setFinancers(data.financers || FALLBACK_FINANCERS);
                  setF({ ...f, financer_name: name });
                  toast.success("Financer added");
                  setAddingFinancer(false); setNewFinancer("");
                } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
              }}
                className="px-3 py-1.5 rounded-md text-sm bg-brand-blue bg-brand-blue-hover text-white"
                data-testid="new-financer-save">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Payments */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-semibold">Payments</h3>
          <button onClick={addPayment} data-testid="add-payment-button"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-brand-orange bg-brand-orange-hover text-white text-xs">
            <Plus className="w-3 h-3" /> Add Payment
          </button>
        </div>
        <div className="space-y-2">
          {(f.payments || []).length === 0 && <div className="text-sm text-slate-400 py-4 text-center">No payments recorded yet</div>}
          {(f.payments || []).map((p, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center bg-slate-50 rounded-md p-2" data-testid={`payment-row-${i}`}>
              <select className="input col-span-2" value={p.mode} onChange={(e) => updatePayment(i, "mode", e.target.value)} data-testid={`payment-mode-${i}`}>
                {MODES.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              <input type="number" className="input col-span-2" placeholder="Amount"
                value={p.amount} onChange={(e) => updatePayment(i, "amount", e.target.value)} data-testid={`payment-amount-${i}`} />
              <input type="date" className="input col-span-2" value={p.date} onChange={(e) => updatePayment(i, "date", e.target.value)} data-testid={`payment-date-${i}`} />
              <input className="input col-span-2" placeholder="Txn ID" value={p.transaction_id || ""} onChange={(e) => updatePayment(i, "transaction_id", e.target.value)} data-testid={`payment-txn-${i}`} />
              <input className="input col-span-3" placeholder="Remarks" value={p.remarks || ""} onChange={(e) => updatePayment(i, "remarks", e.target.value)} data-testid={`payment-remarks-${i}`} />
              <button onClick={() => removePayment(i)} className="p-2 rounded-md hover:bg-red-50 text-red-500" data-testid={`payment-remove-${i}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-6">
          <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 flex items-center justify-between">
            <span className="text-xs uppercase tracking-wider text-emerald-700">Total Payment Received</span>
            <span className="font-heading text-xl font-bold text-emerald-900" data-testid="total-paid">{INR(totals.paid)}</span>
          </div>
          <div className={`rounded-md p-4 flex items-center justify-between border ${totals.remain > 0 ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`}>
            <span className="text-xs uppercase tracking-wider text-slate-600">
              {(f.payment_type || "Loan") === "Cash"
                ? (totals.remain > 0 ? "Outstanding Balance" : "Payment Completed")
                : "Remaining Balance"}
            </span>
            <span className={`font-heading text-xl font-bold ${totals.remain > 0 ? "text-amber-900" : "text-emerald-900"}`} data-testid="remaining-balance">
              {totals.remain > 0 ? INR(totals.remain) : ((f.payment_type || "Loan") === "Cash" ? "✓ Paid" : INR(0))}
            </span>
          </div>
        </div>
      </div>

      <style>{`.input { padding: 0.5rem 0.75rem; border-radius: 0.375rem; border: 1px solid #CBD5E1; font-size: 0.875rem; background: white; outline: none; }`}</style>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-6 mb-6">
    <h3 className="font-heading text-lg font-semibold mb-4">{title}</h3>
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">{children}</div>
  </div>
);

const FormRow = ({ label, children, full }) => (
  <label className={`block ${full ? "col-span-2 md:col-span-3" : ""}`}>
    <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">{label}</div>
    {children}
  </label>
);

const ReadRow = ({ k, v, mono }) => (
  <div>
    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">{k}</div>
    <div className={`bg-slate-50 rounded-md px-3 py-2 text-sm text-slate-900 ${mono ? "font-mono" : ""}`}>{v}</div>
  </div>
);

const PriceInput = ({ label, val, on, testid }) => (
  <label className="block">
    <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">{label}</div>
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₹</span>
      <input type="number" min="0" value={val ?? ""} onChange={on} data-testid={testid}
        className="w-full pl-7 pr-3 py-2 rounded-md border border-slate-300 text-sm outline-none focus:border-blue-800" />
    </div>
  </label>
);

function DocUpload({ label, field, f, setF, testid, vehicleId }) {
  const val = f[field];
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    let revoke = null;
    async function load() {
      if (!val) { setPreview(null); return; }
      if (typeof val === "string" && val.startsWith("data:")) {
        setPreview({ url: val, kind: val.startsWith("data:image") ? "image" : "file" });
        return;
      }
      // storage path — fetch as blob
      try {
        const token = localStorage.getItem("iam_token");
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/files/${val}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: "include",
        });
        if (!res.ok) throw new Error("Load failed");
        const ct = res.headers.get("Content-Type") || "";
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        revoke = url;
        setPreview({ url, kind: ct.startsWith("image/") ? "image" : "file" });
      } catch (e) { setPreview({ url: null, kind: "file" }); }
    }
    load();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [val]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error(`${label} must be under 2 MB`); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/finance/${vehicleId}/upload/${field}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setF({ ...f, [field]: data.path });
      toast.success(`${label} uploaded`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setUploading(false); }
  };
  const clear = () => setF({ ...f, [field]: null });

  return (
    <div className="border border-slate-200 rounded-md p-3">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">{label}</div>
      {val ? (
        <div className="space-y-2">
          {preview?.kind === "image" && preview.url ? (
            <img src={preview.url} alt={label} className="w-full h-32 object-contain bg-slate-50 rounded" />
          ) : (
            <div className="h-32 bg-slate-50 rounded flex items-center justify-center text-xs text-slate-500">
              <FileText className="w-6 h-6 mr-1" /> Document uploaded
            </div>
          )}
          <div className="flex gap-2">
            {preview?.url && (
              <a href={preview.url} target="_blank" rel="noopener noreferrer"
                className="flex-1 text-center text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-50">View</a>
            )}
            <button type="button" onClick={clear} data-testid={`${testid}-remove`}
              className="text-xs px-2 py-1 rounded bg-red-50 text-red-600 hover:bg-red-100">
              <X className="w-3 h-3 inline" /> Remove
            </button>
          </div>
        </div>
      ) : (
        <label className={`cursor-pointer flex flex-col items-center justify-center h-32 border-2 border-dashed border-slate-300 rounded hover:bg-slate-50 text-slate-500 ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
          <Upload className="w-6 h-6 mb-1" />
          <span className="text-xs">{uploading ? "Uploading…" : "Click to upload"}</span>
          <input type="file" accept="image/*,application/pdf" onChange={handleFile}
            className="hidden" data-testid={testid} />
        </label>
      )}
    </div>
  );
}
